/* eslint-disable jsx-a11y/alt-text */
import { Column } from "../../../../core/components/Column";
import { Container } from "../../../../core/components/Container";
import { TextComponent } from "../../../../core/components/TextComponent";
import { ButtonBack, CustomButton } from "../../../../core/components/CustomButton";
import { ErrorMessage, Form, Formik, FormikHelpers } from "formik";
import { OTPFormValues, otpValidationSchema } from "../interfaces/OTPFormInterface";
import 'react-phone-number-input/style.css'
import '../css/inputPhone.css'
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import OtpInput from "react-otp-input";
import { TimerComponent } from "../components/TimerComponent";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../../core/store/store";
import { onLoginUser } from "../store/auth/AuthThunk";
import { ChangeEvent, useState } from "react";
import { ModalSimple } from "../../../../core/components/Modal";
import { CustomInput } from "../../../../core/components/CustomInput";
import { getSafeNextPath } from "../routes/affiliateRedirect";
import { getPostLoginPath } from "../../../../core/utilities/accountRecovery";

const initialValues: OTPFormValues = {
    otp: '',
    password: '',
    confirmPassword: '',
};

export const OTPScreen = () => {
    const navigate = useNavigate();
    const dispatch: AppDispatch = useDispatch();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const { recipient, type } = useParams();
    const [searchParams] = useSearchParams();
    const [isBanned, setIsBanned] = useState(false);
    const mode = searchParams.get("mode") === "reset" ? "reset" : "login";
    const isResetMode = mode === "reset";
    const nextPath = getSafeNextPath(searchParams.get("next"));
    const onBack = () => {
        navigate(-1);
    };

    const handleSubmit = (values: OTPFormValues, { setSubmitting }: FormikHelpers<OTPFormValues>) => {
        setErrorMessage(null);
        setIsBanned(false);
        if (isResetMode && values.password.length < 8) {
            setErrorMessage("Set a new password with at least 8 characters.");
            setSubmitting(false);
            return;
        }
        if ((values.password || values.confirmPassword) && values.password.length < 8) {
            setErrorMessage("Password must be at least 8 characters.");
            setSubmitting(false);
            return;
        }
        if (values.password !== values.confirmPassword) {
            setErrorMessage("Passwords do not match.");
            setSubmitting(false);
            return;
        }
        dispatch(
            onLoginUser({
                emailOrPhone: recipient ?? '',
                type: type ?? '0',
                otp: values.otp,
                isLogin: true,
                password: values.password || undefined,
            })
        ).then((resp) => {
            if (resp.meta.requestStatus === 'fulfilled') {
                navigate(getPostLoginPath((resp as any).payload, nextPath));
            } else {
                if (resp.meta.requestStatus === 'rejected') {
                    if ((resp as any).error.message === "403") {
                        setIsBanned(true);
                        setSubmitting(false);
                        return;
                    }
                }
                setErrorMessage(
                    isResetMode
                        ? "We could not verify the code right now. Please try again."
                        : "Invalid or expired verification code."
                );
            }
        });

        setSubmitting(false);
    };

    return (
        <>
            <Formik
                initialValues={initialValues}
                validationSchema={otpValidationSchema}
                validateOnBlur
                validateOnChange={false}
                onSubmit={handleSubmit}
            >
                {({ setFieldValue, values }) => (
                    <Container
                        className="w-full flex flex-col justify-center md:w-1/2"
                    >
                        <Column
                            crossAxisAlignment="center"
                            className="h-full gap-10"
                        >
                            <img
                                src="/bg-otp.svg"
                                alt="bg-otp"
                                height={200}
                                width={295}
                                className="w-full h-auto block md:hidden"
                            />
                            <Column
                                className="relative h-full mx-8 md:mx-24 lg:w-1/2 md:my-16"
                                mainAxisAlignment="maxmdstart"
                            >
                                <ButtonBack
                                    onClick={onBack}
                                    className="relative pb-6 md:p-0 md:absolute md:top-0"
                                >
                                    Go Back
                                </ButtonBack>
                                <TextComponent
                                    variant="h1"
                                    className="font-bold max-md:text-center"
                                >
                                    OTP verification
                                </TextComponent>
                <TextComponent variant="h6" className="mt-3 text-gray-500">
                                    We have sent a verification code<span className="text-primary font-bold"> {recipient}</span>
                                </TextComponent>
                                <TextComponent variant="h6" className="mt-2 text-gray-500">
                                    {isResetMode
                                        ? "Set your new password now."
                                        : "Create your password now if this is your first login."}
                                </TextComponent>
                                <Form
                                    className="mt-9 md:mt-5"
                                >
                                    <OtpInput
                                        value={values.otp}
                                        onChange={(otp) => setFieldValue('otp', otp)}
                                        numInputs={6}
                                        renderSeparator={<span className="w-2 md:w-4"></span>}
                                        renderInput={(props) => <input {...props} type="tel" />}
                                        inputType="number"
                                        inputStyle={{
                                            width: '3rem',
                                            height: '2.5rem',
                                            padding: '1rem 0.5rem',
                                            borderRadius: '0.5rem',
                                            border: '1px solid #E6E6E6',
                                        }}
                                    />
                                    {errorMessage && <div className="text-red-500 mt-2">{errorMessage}</div>}
                                    <div className="mt-5">
                                        <CustomInput
                                            label={isResetMode ? "Set new password" : "Create password now (optional)"}
                                            hintText="Enter password"
                                            value={values.password}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                setFieldValue('password', e.target.value)
                                            }
                                            obscureText
                                        />
                                    </div>
                                    <div className="mt-4">
                                        <CustomInput
                                            label="Confirm new password"
                                            hintText="Repeat password"
                                            value={values.confirmPassword}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                setFieldValue('confirmPassword', e.target.value)
                                            }
                                            obscureText
                                        />
                                    </div>
                                    <TimerComponent phoneOrEmail={recipient} type={type ?? '0'} />
                                    <ErrorMessage
                                        name="phone"
                                        component="div"
                                        className="text-red-500"
                                    />
                                    <CustomButton
                                        type="submit"
                                        className="mt-9"
                                    >
                                        VERIFY
                                    </CustomButton>
                                </Form>
                            </Column>
                        </Column>
                    </Container>
                )}
            </Formik>
            {isBanned && (
                <ModalSimple
                    actionExecuted={isBanned}
                    title="Account Banned"
                    onClose={() => {
                        setIsBanned(false)
                    }}
                >
                    <TextComponent variant="h5" className="text-center">
                        Your account has been banned. Please contact support for assistance.
                        <span className="font-bold text-primary ml-1 cursor-pointer" onClick={()=>window.open("mailto:info@unjabbed.app")}>
                            info@unjabbed.app
                        </span>
                    </TextComponent>
                </ModalSimple>
            )}
        </>
    );
}
